import React from "react";
import { Bar } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import { IndustryExposure } from "api/api-risk-dashboard.d";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IndustryExposureBarChartProps {
  industryData?: IndustryExposure;
  isLoading?: boolean;
  error?: any;
}

const IndustryExposureBarChart: React.FC<IndustryExposureBarChartProps> = ({ 
  industryData: data,
  isLoading = false,
  error = null
}) => {
  const theme = useTheme();
  
  // Colors for bars
  const colors = [
    "#3F51B5", // Indigo
    "#2196F3", // Blue
    "#00BCD4", // Cyan
    "#009688", // Teal
    "#4CAF50", // Green
    "#8BC34A", // Light Green
    "#CDDC39", // Lime
    "#FFEB3B", // Yellow
    "#FFC107", // Amber
    "#FF9800", // Orange
  ];
  
  // Transform API data for bar chart
  const createChartData = () => {
    if (!data?.data?.industry) return null;
    
    const industries = data.data.industry;
    
    // Sort industries by exposure value (descending)
    const sortedIndustries = [...industries].sort((a, b) => 
      b.average_invoice_amount - a.average_invoice_amount
    );
    
    return {
      labels: sortedIndustries.map((item) => item.industry),
      datasets: [
        {
          label: 'Average Invoice Amount',
          data: sortedIndustries.map((item) => item.average_invoice_amount),
          backgroundColor: sortedIndustries.map((_, index) => colors[index % colors.length]),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  const chartData = createChartData();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !chartData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load industry exposure data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ height: "calc(100% - 50px)" }}>
        <Bar 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    return `$${context.parsed.y.toLocaleString()}`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: {
                  color: theme.palette.divider,
                },
                ticks: {
                  callback: function (value) {
                    return `$${(Number(value) / 1000).toFixed(0)}K`;
                  }
                }
              },
              x: {
                grid: {
                  display: false,
                },
                ticks: {
                  maxRotation: 45,
                  minRotation: 45
                }
              }
            },
            animation: {
              duration: 1000
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default IndustryExposureBarChart; 
